/**
 * Hook for fetching and triggering workflow run failure diagnostics.
 *
 * Supports polling when a diagnostic is pending or running.
 *
 * @module hooks/useRunFailureDiagnostic
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';
import type { FailureDiagnostic } from '@chatsdet/types/issues';

interface FailureDiagnosticData {
  id: string;
  status: string;
  diagnostic: FailureDiagnostic | null;
  createdAt: string;
  updatedAt: string;
}

interface FailureDiagnosticResponse {
  ok: boolean;
  data: FailureDiagnosticData | null;
  error?: string;
}

export function useRunFailureDiagnostic(
  workflowId: string | undefined,
  runId: string | undefined,
  enabled: boolean = false
) {
  const queryClient = useQueryClient();
  const queryKey = ['run-failure-diagnostic', workflowId, runId];

  const query = useQuery<FailureDiagnosticData | null>({
    queryKey,
    queryFn: async () => {
      if (!workflowId || !runId) return null;

      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/failure-diagnostic`,
        { credentials: 'include' }
      );

      if (!res.ok) {
        throw await toApiError(res, 'Failed to load failure diagnostic');
      }

      const json: FailureDiagnosticResponse = await res.json();
      if (!json.ok) {
        throw new Error(json.error ?? 'Failed to load failure diagnostic');
      }

      return json.data;
    },
    enabled: enabled && Boolean(workflowId) && Boolean(runId),
    // Poll when diagnostic is in progress
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'pending' || data.status === 'running')) {
        return 3000;
      }
      return false;
    },
    staleTime: 30_000,
  });

  const triggerMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId || !runId) throw new Error('Missing workflowId or runId');

      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/failure-diagnostic`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        }
      );

      if (!res.ok) {
        throw await toApiError(res, 'Failed to trigger diagnostic');
      }

      const json = await res.json();
      return json.data as { id: string; status: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    status: query.data?.status ?? null,
    diagnostic: query.data?.diagnostic ?? null,
    isLoading: query.isLoading,
    triggerDiagnostic: triggerMutation.mutate,
    isTriggering: triggerMutation.isPending,
  };
}
